import React from "react";
import { Stack } from "@mui/system";
import { Typography, Button, IconButton, styled, Box } from "@mui/material";
import moment from "moment";
import BadgeVisual from "../visuals/BadgeVisual";
import IconButtonWithText from "../inputs/IconButtonWithText";
import IconValueVisual from "../visuals/IconValueVisual";

export interface EventCardListViewProps {
  eventDay: string;
  image?: string;
  eventName: string;
  companyHost: string;
  tag: string;
  time: string;
  location?: string;
  date: string;
  dateRange: string;
  options: { value: string; label: string }[];
  onDetailClick: () => void;
  onFavorite: () => void;
  onLike: () => void;
  onSearch: () => void;
  onShare: () => void;
}

const StyledIconButton = styled(IconButton)(({ theme }: any) => ({
  padding: theme.spacing(1),
  background: theme.palette.common.backgroundGrey,
  "& .far": {
    fontSize: 14
  }
}));

const EventCardListView = ({
  eventDay,
  image,
  eventName,
  companyHost,
  tag,
  time,
  location,
  dateRange,
  date,
  options,
  onDetailClick,
  onFavorite,
  onLike,
  onSearch,
  onShare
}: EventCardListViewProps) => {
  return (
    <Stack border={"2px solid #E7E7E7"} direction="row">
      <Stack
        alignItems="center"
        justifyContent="center"
        maxWidth={134}
        minWidth={120}
      >
        <Typography fontSize={16} fontWeight={400}>
          {eventDay}
        </Typography>
        <Typography variant="h3" color="primary" fontWeight={400}>
          {moment(date).format("MMM").toUpperCase()}
        </Typography>
        <Typography variant="h3" color="primary" fontWeight={400}>
          {moment(date).format("DD")}
        </Typography>
      </Stack>
      <Box
        style={{
          backgroundImage: `url(${image
            ? image
            : `/assets/images/default-img.jpg`})`,
          backgroundRepeat: "no-repeat",
          backgroundSize: "cover",
          backgroundPosition: "center",
          width: 175,
          minWidth: 175
        }}
      />
      <Stack p={2.5} flex={1}>
        <Stack direction="row" spacing={2} mb={2}>
          <Stack spacing={0.5} width="35%">
            <Typography
              variant="body1"
              fontWeight={700}
              lineHeight={1}
              style={{
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "inherit",
                display: "-webkit-box",
                WebkitLineClamp: 3,
                WebkitBoxOrient: "vertical"
              }}
            >
              {eventName}
            </Typography>
            <Typography variant="h4" color="primary" fontWeight={500}>
              {companyHost}
            </Typography>
          </Stack>
          <Stack flex={1} direction="row" justifyContent="space-between">
            <Stack mt={1}>
              <BadgeVisual label={tag} borderPlacement="all" />
            </Stack>
            <Stack mt={1}>
              <IconButtonWithText title="Interested" subOptions={options} />
            </Stack>
            <Stack>
              <Button
                variant="contained"
                color="primary"
                onClick={onDetailClick}
                sx={{ width: 116 }}
              >
                Details
              </Button>
              <Typography variant="h6" fontWeight={500}>
                Industry Categories
              </Typography>
            </Stack>
          </Stack>
        </Stack>
        <Stack direction="row" height="40%">
          <Stack direction="row" spacing={4} flex={1}>
            <IconValueVisual icon="far fa-calendar-alt" value={dateRange} />
            <IconValueVisual icon="far fa-clock" value={`Start: ${time}`} />
            {location &&
              <IconValueVisual
                icon="far fa-map-marker-alt"
                value={location}
                type="location"
              />}
          </Stack>
          <Stack direction="row" spacing={0.5}>
            <StyledIconButton onClick={onFavorite}>
              <i className="far fa-star font-black" />
            </StyledIconButton>
            <StyledIconButton onClick={onShare}>
              <i className="far fa-share-alt font-black" />
            </StyledIconButton>
            <StyledIconButton onClick={onLike}>
              <i className="far fa-thumbs-up font-black" />
            </StyledIconButton>
            <StyledIconButton onClick={onSearch}>
              <i className="far fa-search-location font-black" />
            </StyledIconButton>
          </Stack>
        </Stack>
      </Stack>
    </Stack>
  );
};

export default EventCardListView;
